import { Fragment } from "react";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { formatDateTime } from "../../lib/format";
import { useForexAdmin } from "./api";
import { AdminPager, Drawer, Money, Panel, QueryState, Stat, mono, muted, orDash, useOpen } from "./parts";

// Finance trades (050): what Ginox earned per closed trade. Every field shown.

const at = (v) => formatDateTime(v);
const SIDE = { buy: "Buy", sell: "Sell" };
const MASTER_TYPE = { customer: "Customer", signal: "Signal" };
const side = (v) => SIDE[v] ?? v;
// Cover commission is never income: muted, never coloured as earnings.
const CoverCommission = ({ v }) => <span className={muted}>{orDash(v)}</span>;

const columns = (labels, right) => labels.map((label) => ({ label, align: right.includes(label) ? "right" : undefined }));

const TRADE_COLUMNS = columns(
  ["Login", "Position", "Symbol", "Side", "Lots", "Open time", "Open price", "Close time", "Close price",
    "Client PnL", "Commission earned", "Markup earned", "Swap earned"],
  ["Lots", "Open price", "Close price", "Client PnL", "Commission earned", "Markup earned", "Swap earned"],
);

// `q` is the page's read of the list, so it can show the period read too.
export function ClientTrades({ q, onPage }) {
  return (
    <QueryState q={q}>
      {(d) => (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <Stat label="Trades">{d.total}</Stat>
            <Stat label="Client PnL"><Money value={d.totals.pnl} /></Stat>
            <Stat label="Commission earned"><Money value={d.totals.commission} /></Stat>
            <Stat label="Markup earned"><Money value={d.totals.markup} /></Stat>
            <Stat label="Swap earned"><Money value={d.totals.swap} /></Stat>
          </div>
          <QueueTable
            columns={TRADE_COLUMNS}
            rows={d.entries}
            emptyText="No closed trades in this period."
            renderRow={(r) => (
              <tr key={`${r.login}-${r.position}`}>
                <Td className={mono}>{r.login}</Td>
                <Td className={mono}>{r.position}</Td>
                <Td className={mono}>{r.symbol}</Td>
                <Td className="text-xs">{side(r.side)}</Td>
                <Td align="right" className={mono}>{r.lots}</Td>
                <Td className={muted}>{at(r.openTime)}</Td>
                <Td align="right" className={mono}>{orDash(r.openPrice)}</Td>
                <Td className={muted}>{at(r.closeTime)}</Td>
                <Td align="right" className={mono}>{r.closePrice}</Td>
                <Td align="right"><Money value={r.pnl} /></Td>
                <Td align="right"><Money value={r.commission} /></Td>
                <Td align="right"><Money value={r.markup} /></Td>
                <Td align="right"><Money value={r.swap} /></Td>
              </tr>
            )}
          />
          <AdminPager data={d} onChange={onPage} label="trades" />
        </div>
      )}
    </QueryState>
  );
}

const MASTER_COLUMNS = columns(
  ["Master login", "Master type", "Position", "Symbol", "Side", "Lots", "Open time", "Open price", "Close time",
    "Close price", "Copiers", "Copied lots", "Cover lots", "Client PnL", "Commission earned", "Cover commission",
    "Markup earned", "Swap earned"],
  ["Lots", "Open price", "Close price", "Copiers", "Copied lots", "Cover lots", "Client PnL", "Commission earned",
    "Cover commission", "Markup earned", "Swap earned"],
);

export function MasterTrades({ q, onPage }) {
  const [open, setOpen] = useOpen("trade");
  return (
    <>
      <QueryState q={q}>
        {(d) => (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Master trades">{d.total}</Stat>
              <Stat label="Client PnL"><Money value={d.totals.pnl} /></Stat>
              <Stat label="Commission earned"><Money value={d.totals.commission} /></Stat>
              <Stat label="Cover commission"><CoverCommission v={d.totals.coverCommission} /></Stat>
              <Stat label="Markup earned"><Money value={d.totals.markup} /></Stat>
              <Stat label="Swap earned"><Money value={d.totals.swap} /></Stat>
              <Stat label="Copied lots">{d.totals.copiedLots}</Stat>
              <Stat label="Cover lots">{d.totals.coverLots}</Stat>
            </div>
            <QueueTable
              columns={MASTER_COLUMNS}
              rows={d.entries}
              emptyText="No master trades in this period."
              renderRow={(r) => (
                <tr
                  key={`${r.masterLogin}-${r.position}`}
                  onClick={() => setOpen(`${r.masterLogin}-${r.position}`)}
                  className="cursor-pointer hover:bg-white/[0.02]"
                >
                  <Td className={mono}>{r.masterLogin}</Td>
                  <Td className="text-xs">{MASTER_TYPE[r.masterType] ?? r.masterType}</Td>
                  <Td className={mono}>{r.position}</Td>
                  <Td className={mono}>{r.symbol}</Td>
                  <Td className="text-xs">{side(r.side)}</Td>
                  <Td align="right" className={mono}>{r.lots}</Td>
                  <Td className={muted}>{at(r.openTime)}</Td>
                  <Td align="right" className={mono}>{orDash(r.openPrice)}</Td>
                  <Td className={muted}>{at(r.closeTime)}</Td>
                  <Td align="right" className={mono}>{r.closePrice}</Td>
                  <Td align="right" className={mono}>{r.copiers}</Td>
                  <Td align="right" className={mono}>{r.copiedLots}</Td>
                  <Td align="right" className={mono}>{r.coverLots}</Td>
                  <Td align="right"><Money value={r.pnl} /></Td>
                  <Td align="right"><Money value={r.commission} /></Td>
                  <Td align="right"><CoverCommission v={r.coverCommission} /></Td>
                  <Td align="right"><Money value={r.markup} /></Td>
                  <Td align="right"><Money value={r.swap} /></Td>
                </tr>
              )}
            />
            <AdminPager data={d} onChange={onPage} label="master trades" />
          </div>
        )}
      </QueryState>
      <MasterTradeDrawer id={open} onClose={() => setOpen(null)} />
    </>
  );
}

// One line of a master trade: the master's own, or a copy. Not counted is greyed out.
function Line({ title, l }) {
  const rows = [
    ["Login", l.login],
    ["Position", orDash(l.position)],
    ["Symbol", l.symbol],
    ["Side", side(l.side)],
    ["Lots", orDash(l.lots)],
    ["Open time", at(l.openTime)],
    ["Open price", orDash(l.openPrice)],
    ["Close time", at(l.closeTime)],
    ["Close price", orDash(l.closePrice)],
    ["Client PnL", <Money value={l.pnl} />],
    ["Commission earned", <Money value={l.commission} />],
    ["Markup earned", <Money value={l.markup} />],
    ["Swap earned", <Money value={l.swap} />],
    ["Open", l.open ? "Yes" : "No"],
    ["Counted", l.counted ? "Yes" : "No"],
    ...("skipReason" in l ? [["Skip reason", orDash(l.skipReason)]] : []),
  ];
  return (
    <div className={`rounded-xl border border-border/60 bg-white/[0.02] px-4 py-3 text-sm ${l.counted ? "" : "opacity-50"}`}>
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        {title}
        {l.open && <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-sky-400">Open</span>}
        {l.skipReason && <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-amber-400">{l.skipReason}</span>}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-xs">
        {rows.map(([k, v]) => (
          <Fragment key={k}>
            <span className="text-muted-foreground">{k}</span>
            <span>{v}</span>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function MasterTradeDrawer({ id, onClose }) {
  const [login, position] = id ? id.split("-") : [];
  const q = useForexAdmin(id ? `/finance/admin/master-trades/${login}/${position}` : null);
  return (
    <Drawer open={id} onClose={onClose} title={`Master trade ${login ?? ""} / ${position ?? ""}`}>
      {id && (
        <QueryState q={q}>
          {(d) => (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Master login">{d.masterLogin}</Stat>
                <Stat label="Master type">{MASTER_TYPE[d.masterType] ?? d.masterType}</Stat>
              </div>
              <Line title="Master's own trade" l={d.master} />
              <Panel title={`Copied trades (${d.copies.length})`}>
                {d.copies.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No copied trades.</p>
                ) : (
                  d.copies.map((c, i) => <Line key={`${c.login}-${i}`} title={`Copier ${c.login}`} l={c} />)
                )}
              </Panel>
            </>
          )}
        </QueryState>
      )}
    </Drawer>
  );
}
